import { Suspense } from "react";
import { Outlet, useParams } from "react-router";
import type { RouteObject } from "react-router";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { TranslationProvider, useSyncTranslationConfig } from "@privacy/translations";
import { ThemeProvider } from "@privacy/ui";
import { queryClient } from "../../../api/queryClient";
import type { InviteConversation } from "../../../api/assistant";
import { inviteConversationQueryOptions } from "../../../api/assistant";
import type { AssistantInvitationInfo } from "../../../api/assistantInvitations";
import { assistantInvitationQueryOptions } from "../../../api/assistantInvitations";
import { PageErrorBoundary } from "../../../root/error/ErrorPage";
import { PageSpinner } from "../../../shared/Page";
import { usePrivacyDocumentTitle } from "../../../shared/usePrivacyDocumentTitle";
import { InviteChat } from "./InviteChat";
import { InviteClosed, InviteLogin } from "./InviteLogin";

const InviteProviders = () => {
  const { invitationId } = useParams();

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TranslationProvider>
          <PageErrorBoundary
            authConfig={{ isAuthenticated: false }}
            includePlaceholderSidebar={false}
            homeUrl={`/invite/${invitationId}`}
          >
            <Suspense fallback={<PageSpinner />}>
              <Outlet />
            </Suspense>
          </PageErrorBoundary>
        </TranslationProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

const AssistantInvitePage = () => {
  usePrivacyDocumentTitle("Assistant.Title");
  const { invitationId = "" } = useParams();
  const invitationQuery = useQuery(assistantInvitationQueryOptions(invitationId));
  const conversationQuery = useQuery(inviteConversationQueryOptions(invitationId));

  if (!invitationQuery.data || conversationQuery.isPending) return <PageSpinner />;

  return (
    <InviteSession
      invitationId={invitationId}
      invitation={invitationQuery.data}
      conversation={conversationQuery.isError ? undefined : conversationQuery.data}
    />
  );
};

const InviteSession = ({
  invitationId,
  invitation,
  conversation,
}: {
  invitationId: string;
  invitation: AssistantInvitationInfo;
  conversation?: InviteConversation;
}) => {
  useSyncTranslationConfig({ language: invitation.language });

  if (conversation)
    return (
      <InviteChat
        invitationId={invitationId}
        conversation={conversation}
        organisationName={invitation.organisationName}
      />
    );
  if (invitation.status === "waiting" || invitation.status === "active")
    return <InviteLogin invitationId={invitationId} invitation={invitation} />;
  return <InviteClosed invitation={invitation} />;
};

export const assistantInviteRoute = {
  path: "invite/:invitationId",
  Component: InviteProviders,
  children: [{ index: true, Component: AssistantInvitePage }],
} satisfies RouteObject;
